import SideLayout from '../layouts/SideLayout';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AccountManagementTable from '../components/account-management/AccountManagementTable';
import { AccountManagementButton } from '../components/account-management/AccountManagementButton';
import { AccountManagementTablePagination } from '../components/account-management/AccountManagementTablePagination';
import { AccountManagementInsertModal } from '../components/account-management/AccountManagementInsertModal';
import { fetchToken } from '../utils/cookie';
import { jwtDecode } from 'jwt-decode';
import { del, get } from '../utils/fetch';
import { InitiateAuthResponse } from '@aws-sdk/client-cognito-identity-provider';
import { ForecastAlert } from '@/components/forecast/ForecastAlert';
import SVGImage from '@/components/layout/SVGImage';

export interface UserAttributes {
  sub: string;
  'custom:role'?: string;
  email?: string;
  'custom:companyContact'?: string;
  'custom:companyName'?: string;
  'custom:username'?: string;
  'custom:companyCode'?: string;
  'custom:siteId'?: string;
}

export enum UserRoleEnum {
  User,
  Admin,
  SuperAdmin,
}

export enum ModalTypeEnum {
  CreatedBySuperAdmin,
  CreatedByAdmin,
  Update,
}

interface AccountManagementPageInterface {
  testId?: string;
}

const AccountManagementPage: React.FC<AccountManagementPageInterface> = ({
  testId,
}) => {
  const { t } = useTranslation();
  const tablePresentationUnit = 10;
  const [currentIndex, setCurrentIndex] = useState<number | null>(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState<
    Array<AccountManagementData>
  >([]);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<number>(
    ModalTypeEnum.CreatedByAdmin
  );
  const [companyData, setCompanyData] = useState<CompanyEntity>();
  const [showOverUserCountLimitation, setShowOverUserCountLimitation] =
    useState<boolean>(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [loading, setLoading] = useState(false);

  const setLoggedInUserData = () => {
    const decoded_token = jwtDecode<UserAttributes>(
      fetchToken('idToken') as string
    );
    return {
      customRole: decoded_token['custom:role'],
      customContact: decoded_token['custom:companyContact'],
      companyName: companyData?.name,
      companyCode: decoded_token['custom:companyCode'],
      sub: decoded_token['sub'],
      name: decoded_token['custom:username'],
      customSiteId: decoded_token['custom:siteId'],
    };
  };

  const [userData, setUserData] = useState<InitialUser>();
  const loggedInUser: InitialUser = setLoggedInUserData();

  useEffect(() => {
    const fecthColleageData = async () => {
      const response = await get<{ data: Array<AccountManagementData> }>(
        '/auth/colleague'
      );
      if (response.status === 'success' && response.data) {
        setFilteredData(response.data);
      }
    };
    const fetchCompanyData = async () => {
      const response = await get<{ data: CompanyEntity }>('/customer/company');
      if (response.status === 'success' && response.data) {
        setCompanyData(response.data);
      }
    };
    fecthColleageData();
    fetchCompanyData();
    setShowOverUserCountLimitation(false);
  }, [reloadTrigger]);

  const searchData = () => {
    // search by email
    setFilteredData(
      filteredData.filter((userData) =>
        userData.email.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    setCurrentIndex(0);
  };

  const handleEditButtonClick = (user: AccountManagementData) => {
    setModalType(ModalTypeEnum.Update);
    setUserData({
      customRole: String(user.customRole),
      customContact: user.customCompanyContact,
      companyName: user.companyName,
      companyCode: user.customCompanyCode,
      email: user.email,
      name: user.customUsername,
      customSiteId: user.customSiteId,
    });
    setShowModal(true);
  };

  const handleDeleteButtonClick = async (sub: string) => {
    try {
      setLoading(true);
      const requestBody = {
        sub: sub,
      };

      const response = await del<{ data: InitiateAuthResponse }>('/auth/user', {
        body: JSON.stringify(requestBody),
      });
      if (response.status === 'success') {
        console.log('delete success');
      } else {
        console.log('delete failed');
      }
    } catch (error) {
      console.log(`delete failed : ${error}`);
    } finally {
      setLoading(false);
      setReloadTrigger((prev) => prev + 1);
    }
  };

  const handleModalOnReturned = () => {
    setShowModal(false);
    setReloadTrigger((prev) => prev + 1);
  };

  return (
    <SideLayout>
      {loading && (
        <div className="fixed left-0 top-0 z-10 flex h-full w-full items-center justify-center bg-gray-500 opacity-50">
          <SVGImage name="dropdown-spinner" />
        </div>
      )}
      <div
        data-testid={testId}
        className="flex min-h-screen min-w-[1200px] flex-col whitespace-nowrap bg-light-2 px-[50px] py-[50px] [&>*]:mx-auto [&>*]:w-full [&>*]:max-w-[1100px]"
      >
        <header>
          <p className="pop-semibold-5 leading-none text-[#1F384C]">
            계정 관리
          </p>
        </header>
        <section className="mt-[30px] h-[884px] w-[1094px] rounded-[10px] bg-white px-[30px] py-[40px]">
          <div className="flex items-center justify-between">
            <form
              className="noto-15-medium leading-none"
              onSubmit={(e) => e.preventDefault()}
            >
              <input
                className="left-[317px] top-[125px] h-[36px] w-[320px] rounded-[5px] bg-[#F3F6F8] pl-[10px] text-[14px] text-[#333] focus:outline-none"
                name="account-filter"
                value={searchTerm}
                placeholder="계정 검색"
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button
                className="noto-13-medium left-[647px] top-[125px] ml-[10px] h-[36px] w-[100px] cursor-pointer rounded-[5px] border bg-[#00748E] text-[#FFFFFF]"
                onClick={searchData}
              >
                검색
              </button>
            </form>
            {loggedInUser.customRole !== String(UserRoleEnum.User) && (
              <AccountManagementButton
                testId="add-account"
                text={t('accountManagement.addAccount')}
                onClick={() => {
                  if (
                    loggedInUser.customRole === String(UserRoleEnum.SuperAdmin)
                  ) {
                    // super admin
                    setUserData({}); // empty user
                    setShowModal(true);
                    setModalType(ModalTypeEnum.CreatedBySuperAdmin);
                  } else {
                    // admin
                    if (filteredData.length < 5) {
                      setUserData(setLoggedInUserData());
                      setShowModal(true);
                      setModalType(ModalTypeEnum.CreatedByAdmin);
                    } else {
                      setShowOverUserCountLimitation(true);
                      setShowModal(false);
                    }
                  }
                }}
              />
            )}
          </div>
          {showOverUserCountLimitation && (
            <div className="mt-[10px] flex justify-end">
              <ForecastAlert message="계정은 5명까지 생성 가능합니다." />
            </div>
          )}
          <AccountManagementTable
            testId={`${testId}-table`}
            loggedInUser={loggedInUser}
            data={
              currentIndex === null
                ? []
                : filteredData.slice(
                    currentIndex * tablePresentationUnit,
                    currentIndex * tablePresentationUnit +
                      tablePresentationUnit >
                      filteredData.length
                      ? filteredData.length
                      : currentIndex * tablePresentationUnit +
                          tablePresentationUnit
                  )
            }
            onEditButtonClick={handleEditButtonClick}
            onDeleteButtonClick={handleDeleteButtonClick}
            className="mt-[20px]"
          ></AccountManagementTable>
          <AccountManagementTablePagination
            className="mt-4"
            currentIndex={currentIndex}
            setIndex={setCurrentIndex}
            length={Math.ceil(filteredData.length / tablePresentationUnit)}
            testId="gridus-pagination"
          />
        </section>
      </div>
      {showModal && (
        <section
          data-testid="add-account-modal-background"
          className="absolute left-0 top-0 flex h-full w-full items-center justify-center bg-black bg-opacity-80"
        >
          <AccountManagementInsertModal
            onReturned={handleModalOnReturned}
            testId="add-account-modal"
            initialUserData={userData}
            type={modalType}
          ></AccountManagementInsertModal>
        </section>
      )}
    </SideLayout>
  );
};

export default AccountManagementPage;
